'use client'

import { useTranslations } from 'next-intl'
import { useRequiredAppUpdate } from '@/hooks/use-required-app-update'

/**
 * App Update Modal — shown when the native app version is below
 * the server's minimum required version.
 *
 * This is harmless to ship before the app is on the store:
 * - On web: never renders (isNativePlatform() returns false)
 * - On native with no API response: never renders
 * - Only triggers when BOTH conditions are true:
 *   1. Running in a native WebView
 *   2. Server responds with a minimum version higher than the native app version
 *
 * The version check hits /api/app-version which returns { minimumVersion: "1.0.0" }
 * The native app version is read from the Capacitor App plugin.
 * The check lives in `useRequiredAppUpdate` (shared with the Encuentro modal).
 */
export function AppUpdateModal() {
  const t = useTranslations('common')
  const storeUrl = useRequiredAppUpdate()

  if (storeUrl === null) return null

  return (
    <div className="fixed inset-0 z-10000 flex items-center justify-center bg-black/60 p-6">
      <div className="mx-auto w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
          <svg
            className="h-7 w-7 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
            />
          </svg>
        </div>

        <h2 className="mb-2 text-xl font-semibold text-gray-900">{t('updateRequired')}</h2>
        <p className="mb-6 text-sm text-gray-600">{t('updateDescription')}</p>

        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          {t('updateNow')}
        </a>
      </div>
    </div>
  )
}
